import { ReactChild } from 'react';

import { TTranslator } from '@smartshell/ui-kit';

import { TLocalesKeys } from 'localization';
import { openConfirmDialog } from 'services/Confirmation';
import {
  TConfirmationType,
  TConfirmStatus,
} from 'services/Confirmation/interfaces';
import { EPaymentMethod } from 'types';

import {
  ECommand,
  ECommandStatus,
  EGoodCodeType,
  ENDS,
  EReceiptType,
  ESignMethod,
  ESignObject,
  ETaxVariant,
  IFiscalReceiptItem,
  IGoodCodeDataRequest,
  IGoodCodeDataResponse,
  IKKMListResponse,
  IKKMRequest,
  IKKMResponse,
  IKKMStateResponse,
  IPrintReceiptRequest,
  ISaleItem,
  TNDSValue,
  TTaxVariantValue,
} from './types';

const TAX_VARIANT_MAP: Record<ETaxVariant, TTaxVariantValue> = {
  [ETaxVariant.MAIN]: 0,
  [ETaxVariant.SIMPLE_INCOME]: 1,
  [ETaxVariant.SIMPLE_INCOME_EXPENSES]: 2,
  [ETaxVariant.PATENT]: 5,
};
const NDS_MAP: Record<ENDS, TNDSValue> = {
  [ENDS.NO_NDS]: -1,
  [ENDS.NDS_0]: 0,
  [ENDS.NDS_5]: 5,
  [ENDS.NDS_7]: 7,
  [ENDS.NDS_10]: 10,
  [ENDS.NDS_20]: 22,
  [ENDS.NDS_110]: 110,
  [ENDS.NDS_120]: 120,
};

const generateGUID = (): string => {
  function S4(): string {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  }
  return (
    S4() +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    '-' +
    S4() +
    S4() +
    S4()
  );
};

interface IShowErrorArgs {
  message: ReactChild;
  title?: string;
  buttonText?: string;
  type?: TConfirmationType;
}

interface IGetSumsArgs {
  type: EReceiptType;
  paymentMethod: EPaymentMethod;
  totalPrice: number;
  cardSum?: number;
  cashSum?: number;
}

interface IPrintCheckArgs {
  type: EReceiptType;
  paymentMethod: EPaymentMethod;
  totalPrice: number;
  cardSum?: number;
  cashSum?: number;
  items: Array<ISaleItem>;
  taxVariant?: ETaxVariant | null;
}

export class CashboxService {
  private operatorName: string | null;
  private INN: string | null;
  private license: string | null;
  private t: TTranslator<TLocalesKeys>;

  constructor() {
    this.operatorName = null;
    this.INN = null;
    this.license = null;
    this.t = (key) => key;
  }

  public setLicense = (license: string | null): void => {
    this.license = license;
  };

  public setOperator = (fio: string | null): void => {
    this.operatorName = fio;
  };

  public setCashboxINN = (inn: string | null): void => {
    this.INN = inn;
  };

  public setTranslator = (t: TTranslator<TLocalesKeys>): void => {
    this.t = t;
  };

  private generateUUID = (): string =>
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : generateGUID();

  private showError = ({
    buttonText,
    type = 'retry',
    title,
    message,
  }: IShowErrorArgs): Promise<{ status: TConfirmStatus }> =>
    openConfirmDialog(this.t, type, {
      title: title ?? 'Ошибка кассовой операции',
      message,
      acceptTitle: buttonText,
    });

  private executeCommand = <
    Response extends IKKMResponse,
    Request extends IKKMRequest = IKKMRequest
  >(
    Data: Request
  ): Promise<Response> => {
    if (!window.KkmServer) {
      return this.showError({
        message: (
          <div>
            Для работы кассы установите расширение ККМ для браузера по{' '}
            <a
              href="https://kkmserver.ru/WiKi/Settings"
              target="_blank"
              rel="noreferrer"
            >
              ссылке.
            </a>{' '}
            После установки перезагрузите страницу.
          </div>
        ),
        title: 'Установите расширение',
        buttonText: 'Перезагрузить',
      }).then(({ status }) => {
        if (status === 'ACCEPTED') {
          location.reload();
        }

        return Promise.reject('Отсутствует расширение для браузера');
      });
    }

    if (Data.Command !== ECommand.List && (!this.INN || !this.operatorName)) {
      return this.showError({
        message: 'Выберите кассовый аппарат в настройках SmartShell',
        title: 'Проверьте настройки кассы',
        type: 'notify',
        buttonText: 'Понятно',
      }).then(() => {
        return Promise.reject('Отсутствуют настройки кассы');
      });
    }

    const res: Promise<Response> = new Promise((resolve, reject) => {
      const id = this.generateUUID();

      const dataForExecute = {
        IdCommand: id,
        ...Data,
      };

      if (Data.Command !== ECommand.List) {
        dataForExecute.InnKkm = this.INN ?? undefined;
        dataForExecute.CashierName = this.operatorName ?? undefined;
      }

      if (this.license) {
        dataForExecute.KeySubLicensing = this.license;
      }

      window.KkmServer.Execute((response: Response): void => {
        if (response.Status === ECommandStatus.SUCCESS) {
          return resolve(response);
        }
        reject(response);
      }, dataForExecute);
    });

    return res
      .then((response) => Promise.resolve(response))
      .catch((err) =>
        this.showError({
          message:
            err.Message?.includes('fetch') || err.Error?.includes('fetch') ? (
              <span>
                Модуль KKM-Server не обнаружен.
                <br />
                Запустите KKM-Server и повторите попытку.
              </span>
            ) : (
              <span>
                {err.Message}
                {err.Message && <br />}
                {err.Error}
              </span>
            ),
          type: 'notify',
          title: 'Ошибка обмена с ККТ',
          buttonText: 'Понятно',
        }).then(() => Promise.reject('Модуль KKM-Server не обнаружен.'))
      );
  };

  public validateHonestSign = async (
    code: string
  ): Promise<{
    validate: boolean;
    errorMessage?: string;
  }> => {
    console.log('code', code);

    const response = await this.executeCommand<
      IGoodCodeDataResponse,
      IGoodCodeDataRequest
    >({
      Command: ECommand.GetGoodCodeData,
      BarCode: code,
    });

    console.info('validateHonestSignResponse', response);

    return {
      validate:
        response.DataProductCode.isParsed &&
        response.DataProductCode.ProductCodeType === EGoodCodeType.GS1 &&
        response.ValidationPR.ValidationResult,
      errorMessage: !response.ValidationPR.ValidationResult
        ? response.ValidationPR.DecryptionResult
        : undefined,
    };
  };

  public printXReport = (): Promise<IKKMResponse> =>
    this.executeCommand({ Command: ECommand.XReport });

  public loadKKMs = (): Promise<IKKMListResponse> =>
    this.executeCommand<IKKMListResponse>({ Command: ECommand.List });

  public openWorkShift = (): Promise<IKKMResponse> =>
    this.executeCommand({
      Command: ECommand.OpenShift,
    });

  public closeWorkShift = (): Promise<IKKMResponse> =>
    this.executeCommand({
      Command: ECommand.CloseShift,
    });

  public getState = (): Promise<IKKMStateResponse> =>
    this.executeCommand<IKKMStateResponse>({ Command: ECommand.GetState });

  private getSums = ({
    cardSum = 0,
    cashSum = 0,
    paymentMethod,
    totalPrice,
  }: IGetSumsArgs): Pick<
    IPrintReceiptRequest,
    'ElectronicPayment' | 'Cash'
  > => {
    if (paymentMethod === EPaymentMethod.CARD) {
      return {
        Cash: 0,
        ElectronicPayment: totalPrice,
      };
    }

    if (paymentMethod === EPaymentMethod.CASH) {
      return {
        Cash: totalPrice,
        ElectronicPayment: 0,
      };
    }

    return {
      Cash: cashSum ? cashSum : totalPrice - cardSum,
      ElectronicPayment: cardSum,
    };
  };

  public printCheck = ({
    type,
    paymentMethod,
    totalPrice,
    items,
    taxVariant,
    cardSum,
    cashSum,
  }: IPrintCheckArgs): Promise<IKKMResponse> => {
    if (!taxVariant) {
      return this.showError({
        message: 'Укажите систему налогообложения в настройках SmartShell',
        title: 'Проверьте настройки кассы',
        type: 'notify',
        buttonText: 'Понятно',
      }).then(() => {
        return Promise.reject('Отсутствуют настройки кассы');
      });
    }

    const CheckStrings: IPrintReceiptRequest['CheckStrings'] = [];

    for (const item of items) {
      if (!item.NDS) {
        return this.showError({
          message: 'Укажите тип налога в настройках SmartShell',
          title: 'Проверьте настройки кассы',
          type: 'notify',
          buttonText: 'Понятно',
        }).then(() => {
          return Promise.reject('Отсутствуют настройки кассы');
        });
      }

      let SignCalculationObject = ESignObject.SERVICE;

      if (item.type === 'GOOD') {
        SignCalculationObject = ESignObject.GOOD;

        if (item.is_excise) {
          SignCalculationObject = ESignObject.EXCISE;
        }
      }

      const Register: IFiscalReceiptItem['Register'] = {
        Name: item.title,
        Quantity: item.amount,
        Price: item.price,
        Amount: item.totalPrice ?? item.price,
        Tax: NDS_MAP[item.NDS],
        SignMethodCalculation: ESignMethod.FULL_SETTLEMENT,
        SignCalculationObject,
      };

      if (item.type === 'GOOD' && item.sign) {
        Register.GoodCodeData = { BarCode: item.sign };
      }

      CheckStrings.push({ Register });
    }

    return this.executeCommand<IKKMResponse, IPrintReceiptRequest>({
      Command: ECommand.RegisterCheck,
      Timeout: paymentMethod === EPaymentMethod.CASH ? 15 : 180,
      IsFiscalCheck: true,
      TaxVariant: TAX_VARIANT_MAP[taxVariant],
      TypeCheck: type,
      NumberCopies: 0,
      CheckStrings,
      ...this.getSums({ paymentMethod, cardSum, totalPrice, cashSum, type }),
    });
  };
}
